import React, { type ComponentProps, type RefObject, useCallback, useId } from "react";

import { Box, type BoxRef, Flex } from "../../../../mads";
// Note: This file is a copy of the ChipFormField component from the IFL library.
import { Chip } from "./Chip";
import { FormField } from "./FormField";

type ChipSize = "sm" | "md" | "lg";
type ChipFormFieldItem = Pick<ComponentProps<typeof Chip>, "label" | "value" | "buttonRef">;

export interface ChipFormFieldProps {
  /**
   * An array of props/attributes for the Chip components to render in this form field.
   */
  items: ChipFormFieldItem[];
  /**
   * the label to display on the form field
   */
  label: string;
  /**
   * whether the form data for this field is invalid
   */
  invalid?: boolean;
  /**
   * whether this field is disabled
   */
  disabled?: boolean;
  /**
   * optional helper text for the label component
   */
  helperText?: string;
  /**
   * optional error text for the label component
   */
  errorText?: string;
  /**
   * optional showRequiredIndicator for the label component
   */
  showRequiredIndicator?: boolean;
  /**
   * Size of the Chip. Use the values `sm`, `md`, or `lg`.
   */
  size?: ChipSize;
  /**
   * The value of the currently checked chip option, leave as undefined to represent none selected
   */
  checkedValue: string | undefined;
  /**
   * Callback for when the checked chip changes, does not fire if the selected chip is pressed again
   *   - passes the selected chip value as a parameter
   */
  onChange: (value: string) => void;
  /**
   * Optional accessibility hint to provide context for screen readers on each chip.
   */
  accessibilityHint?: string;
  /**
   * Optional ref to the first chip's Button for focus management (WCAG focus order).
   * Ignored for a given chip if that item supplies its own `buttonRef`.
   */
  firstChipRef?: RefObject<BoxRef | null>;
  /**
   * Optional test id prefix; each chip gets `${testIdPrefix}-${value}` (e.g. for e2e selectors).
   */
  testIdPrefix?: string;
}

/**
 * React Native implementation of ChipFormField
 *
 * N.B. we only implement the 'single' type here.
 *
 * @see https://experience.indeed.design/components/ids/chip-form-field
 */
export function ChipFormField({
  items,
  label,
  invalid,
  disabled,
  helperText,
  errorText,
  size,
  showRequiredIndicator,
  checkedValue,
  onChange,
  accessibilityHint,
  firstChipRef,
  testIdPrefix,
}: ChipFormFieldProps) {
  // Stable per-instance name so the web variant (Chip.web) groups its chips into one native radio
  // group (arrow-key selection + single roving tab stop). Ignored on native.
  const groupName = useId();

  const onSelect = useCallback(
    (value: string) => {
      if (checkedValue === value) {
        return;
      }
      onChange(value);
    },
    [checkedValue, onChange],
  );

  return (
    <FormField
      label={label}
      helperText={helperText}
      errorText={errorText}
      invalid={invalid}
      disabled={disabled}
      showRequiredIndicator={showRequiredIndicator}
    >
      <Box accessibilityRole="radiogroup" accessibilityLabel={label}>
        <Flex sx={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
          {items.map(({ label, value, buttonRef: itemButtonRef }, index) => (
            <Chip
              key={index}
              buttonRef={itemButtonRef ?? (index === 0 ? firstChipRef : undefined)}
              size={size}
              invalid={invalid}
              disabled={disabled}
              label={label}
              checked={value === checkedValue}
              onSelect={onSelect}
              value={value}
              groupName={groupName}
              accessibilityHint={accessibilityHint}
              testID={testIdPrefix ? `${testIdPrefix}-${value}` : undefined}
            />
          ))}
        </Flex>
      </Box>
    </FormField>
  );
}

ChipFormField.displayName = "ChipFormField";
